// src/components/editors/LayoutSelector.js
import React from 'react';
import { Check } from 'lucide-react';

/**
 * LayoutSelector - Reusable layout template selector with live preview
 *
 * @param {string} selectedLayout - Currently selected layout ID
 * @param {function} onChange - Callback when selection changes
 * @param {object} colors - Color object for preview rendering
 * @param {array} layoutOptions - Array of layout option objects with preview configs
 */
const LayoutSelector = ({
  selectedLayout,
  onChange,
  colors,
  layoutOptions
}) => {
  /**
   * Mini Preview Component for each layout style
   */
  const LayoutMiniPreview = ({ styleId, style }) => {
    const preview = style.preview;

    return (
      <div
        className={`overflow-hidden ${preview.containerClass} ${preview.shadowClass}`}
        style={{ backgroundColor: colors.background }}
      >
        {/* Mini Nav */}
        <div
          className={`${preview.navPadding} flex items-center justify-between`}
          style={{
            backgroundColor: styleId === 'minimal' ? colors.background : colors.primary,
            borderBottom: styleId === 'minimal' ? `1px solid ${colors.primary}20` : 'none'
          }}
        >
          <div
            className={`h-2 w-12 ${preview.buttonClass} opacity-90`}
            style={{ backgroundColor: styleId === 'minimal' ? colors.text : 'white' }}
          />
          <div className="flex items-center gap-1.5">
            <div
              className={`h-1.5 w-6 ${preview.buttonClass} opacity-50`}
              style={{ backgroundColor: styleId === 'minimal' ? colors.text : 'white' }}
            />
            <div
              className={`h-1.5 w-6 ${preview.buttonClass} opacity-50`}
              style={{ backgroundColor: styleId === 'minimal' ? colors.text : 'white' }}
            />
            <div
              className={`h-4 w-10 ${preview.buttonClass}`}
              style={{
                backgroundColor: styleId === 'minimal' ? colors.primary : 'white',
                opacity: 0.9
              }}
            />
          </div>
        </div>

        {/* Mini Hero */}
        <div
          className={`${preview.spacingClass} text-center`}
          style={{ backgroundColor: colors.primary + '15' }}
        >
          <div
            className={`h-2.5 w-20 mx-auto ${preview.buttonClass} mb-1.5`}
            style={{ backgroundColor: colors.text, opacity: 0.8 }}
          />
          <div
            className={`h-1.5 w-28 mx-auto ${preview.buttonClass} mb-2.5`}
            style={{ backgroundColor: colors.text, opacity: 0.4 }}
          />
          <div
            className={`h-5 w-14 mx-auto ${preview.buttonClass}`}
            style={{ backgroundColor: colors.primary }}
          />
        </div>

        {/* Mini Content */}
        <div className={`${preview.spacingClass} ${preview.sectionGap} flex flex-col`}>
          {/* Section header placeholder */}
          <div className="space-y-1.5">
            <div
              className={`h-1.5 w-14 ${preview.buttonClass}`}
              style={{ backgroundColor: colors.text, opacity: 0.7 }}
            />
            <div
              className={`h-1 w-full ${preview.buttonClass}`}
              style={{ backgroundColor: colors.text, opacity: 0.15 }}
            />
            <div
              className={`h-1 w-3/4 ${preview.buttonClass}`}
              style={{ backgroundColor: colors.text, opacity: 0.15 }}
            />
          </div>

          {/* Service cards placeholder */}
          <div className="grid grid-cols-2 gap-1.5">
            {[0, 1].map(i => (
              <div
                key={i}
                className={`h-10 ${preview.containerClass}`}
                style={{
                  backgroundColor: colors.background,
                  border: styleId === 'minimal' ? `1px solid ${colors.text}15` : 'none',
                  boxShadow: styleId === 'warm' ? '0 2px 8px rgba(0,0,0,0.05)' :
                             styleId === 'bold' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Layout Style
      </label>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {layoutOptions.map((layout) => {
          const isSelected = selectedLayout === layout.id;
          return (
            <button
              key={layout.id}
              type="button"
              onClick={() => onChange(layout.id)}
              className={`relative rounded-lg border-2 transition-all overflow-hidden ${
                isSelected
                  ? 'border-purple-600 ring-2 ring-purple-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2 z-10 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center shadow-md">
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
              )}

              {/* Preview */}
              <div className="aspect-[4/5]">
                <LayoutMiniPreview styleId={layout.id} style={layout} />
              </div>

              {/* Label */}
              <div className={`p-3 text-center border-t ${isSelected ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`text-sm font-semibold ${isSelected ? 'text-purple-700' : 'text-gray-900'}`}>
                  {layout.name}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {layout.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LayoutSelector;
